import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Calendar, Clock, Tag, ArrowLeft, Share2, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import AuthorAvatar from "../../components/AuthorAvatar";
import RelatedPosts from "../../components/RelatedPosts";
import { Link as RouterLink } from "react-router-dom";
import SEO from "../../components/SEO";

/**
 * Single blog post — public, SEO-critical. Renders sanitized HTML from
 * the backend markdown engine. Injects Article structured data + per-post
 * OG meta tags into <head> for snippet-friendly social sharing.
 */
export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/blog/posts/${slug}`)
      .then((r) => {
        if (cancelled) return;
        setPost(r.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setNotFound(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Post not found</h1>
          <p className="text-slate-600 mt-2">The article you're looking for has moved or doesn't exist.</p>
          <button
            onClick={() => navigate("/blog")}
            className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:text-emerald-900"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all articles
          </button>
        </div>
      </div>
    );
  }

  if (!post) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  }

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  // Build Article JSON-LD with proper Person author for E-E-A-T
  const ap = post.author_profile;
  const origin = typeof window !== "undefined" ? window.location.origin : "https://realtyvaultmgr.com";
  const authorNode = ap
    ? {
        "@type": "Person",
        name: ap.name,
        url: `${origin}/blog/author/${ap.slug}`,
        jobTitle: ap.role,
        image: ap.avatar_url?.startsWith("http") ? ap.avatar_url : `${origin}${ap.avatar_url}`,
        ...((ap.twitter || ap.linkedin)
          ? { sameAs: [ap.twitter, ap.linkedin].filter(Boolean) }
          : {}),
      }
    : { "@type": "Organization", name: post.author };

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    author: authorNode,
    datePublished: post.date,
    dateModified: post.date,
    publisher: {
      "@type": "Organization",
      name: "RealtyVault Manager",
      url: "https://realtyvaultmgr.com",
      logo: {
        "@type": "ImageObject",
        url: "https://realtyvaultmgr.com/marketing/og-share.jpg",
      },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": post.url },
    keywords: (post.tags || []).join(", "),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://realtyvaultmgr.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://realtyvaultmgr.com/blog" },
      { "@type": "ListItem", position: 3, name: post.title, item: post.url },
    ],
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={post.title}
        description={post.description}
        path={`/blog/${slug}`}
        type="article"
        article={{
          publishedTime: post.date,
          modifiedTime: post.date,
          author: ap?.name || post.author,
          tags: post.tags || [],
        }}
        jsonLd={[articleJsonLd, breadcrumbJsonLd]}
      />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-bold text-lg text-slate-900 tracking-tight" style={{ fontFamily: "Outfit" }}>
            RealtyVault Manager
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/blog" className="text-slate-600 hover:text-slate-900">Blog</Link>
            <Link to="/login" className="text-slate-600 hover:text-slate-900">Sign in</Link>
            <Link to="/signup" className="bg-slate-900 text-white px-3 py-1.5 rounded-sm font-semibold text-xs hover:bg-slate-700">Start free trial</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900 mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> All articles
        </Link>

        <article data-testid="blog-post-article">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500 mb-3">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(post.date)}</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {post.reading_time_min} min read</span>
            {post.author_profile ? (
              <RouterLink
                to={`/blog/author/${post.author_profile.slug}`}
                className="inline-flex items-center gap-1.5 hover:text-slate-900 transition-colors"
                data-testid="blog-post-byline"
              >
                <AuthorAvatar author={post.author_profile} size="sm" />
                <span>by <span className="font-medium text-slate-700">{post.author}</span></span>
              </RouterLink>
            ) : (
              <span>by {post.author}</span>
            )}
          </div>

          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-6">
              {post.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-widest text-slate-500 bg-slate-100 px-2 py-0.5 rounded-sm font-semibold">
                  <Tag className="w-2.5 h-2.5" /> {t}
                </span>
              ))}
            </div>
          )}

          <div
            className="prose prose-slate max-w-none prose-headings:font-bold prose-headings:tracking-tight prose-h1:text-4xl prose-h1:mb-4 prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-3 prose-h3:text-xl prose-h3:mt-6 prose-a:text-emerald-700 prose-a:no-underline hover:prose-a:underline prose-strong:text-slate-900 prose-li:my-1 prose-code:text-rose-700 prose-code:before:content-none prose-code:after:content-none prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-pre:bg-slate-900 prose-pre:text-slate-100"
            style={{ fontFamily: "Outfit, sans-serif" }}
            dangerouslySetInnerHTML={{ __html: post.html }}
            data-testid="blog-post-html"
          />

          <div className="mt-12 pt-6 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3">
            <Link to="/blog" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900">
              <ArrowLeft className="w-3.5 h-3.5" /> More articles
            </Link>
            <button
              onClick={async () => {
                try {
                  if (navigator.share) {
                    await navigator.share({ title: post.title, url: shareUrl });
                  } else {
                    await navigator.clipboard.writeText(shareUrl);
                    toast.success("Link copied to clipboard");
                  }
                } catch { /* user cancelled */ }
              }}
              className="inline-flex items-center gap-1.5 text-sm font-semibold border border-slate-300 hover:border-slate-900 text-slate-700 hover:text-slate-900 px-3 py-1.5 rounded-sm"
              data-testid="blog-post-share"
            >
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
          </div>

          {post.author_profile && post.author_profile.bio && (
            <div className="mt-8 bg-white border border-slate-200 rounded-sm p-5 flex items-start gap-4" data-testid="blog-post-author-card">
              <AuthorAvatar author={post.author_profile} size="lg" linkToProfile />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                  Written by · {post.author_profile.role}
                </div>
                <RouterLink
                  to={`/blog/author/${post.author_profile.slug}`}
                  className="text-lg font-bold text-slate-900 hover:text-emerald-700 transition-colors"
                  style={{ fontFamily: "Outfit" }}
                >
                  {post.author_profile.name}
                </RouterLink>
                <p className="text-sm text-slate-600 mt-1.5">{post.author_profile.bio}</p>
                <RouterLink
                  to={`/blog/author/${post.author_profile.slug}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900 mt-2"
                  data-testid="blog-post-author-more"
                >
                  More from this author <ArrowRight className="w-3 h-3" />
                </RouterLink>
              </div>
            </div>
          )}
        </article>

        <RelatedPosts slug={slug} />

        <div className="mt-10 bg-emerald-600 text-white rounded-sm p-6 text-center">
          <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
            Manage your portfolio like the pros.
          </h3>
          <p className="text-sm opacity-90 mt-2 max-w-md mx-auto">
            One platform for properties, leases, loans, AI lease drafting, and one-click Lender Packages.
            14-day free trial — no credit card required.
          </p>
          <Link to="/signup" className="inline-block mt-4 bg-white text-emerald-700 hover:bg-emerald-50 px-5 py-2.5 rounded-sm font-bold text-sm">
            Start free trial
          </Link>
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white mt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-2">
          <span>© 2026 RealtyVault Manager</span>
          <Link to="/" className="hover:text-slate-900">Home</Link>
        </div>
      </footer>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}
